import React from 'react'
import { Badge, Skeleton, StatCards, Table, Td, Th, Tr } from '@/shared/ui'
import { formatMoneyFromCents } from '@/shared/i18n/money'
import { getRevshareUserMonthlySummary, type RevshareMonthlySummaryRow } from '../services/revshareSummaryService'
import { bpsToPercent } from '../utils/percent'
import { monthStartDateToLabel } from '../utils/month'

export default function RevshareMySummaryPanel({ userId }: { userId: string }) {
  const [rows, setRows] = React.useState<RevshareMonthlySummaryRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  async function load() {
    setLoading(true)
    setError(null)
    try {
      const data = await getRevshareUserMonthlySummary(userId, 12)
      setRows(data)
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar resumo')
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  React.useEffect(() => { load() }, [userId])

  if (loading && rows.length === 0) {
    return (
      <div className="space-y-4">
        <StatCards
          items={Array.from({ length: 3 }).map(() => ({ label: '-', value: <Skeleton className="h-6 w-28" /> }))}
          className="lg:grid-cols-3"
        />
        <div className="text-sm text-foreground/70">Carregando...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
        {error}
      </div>
    )
  }

  const currentMonthStart = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1))
    .toISOString()
    .slice(0, 10)
  const currentRow = rows.find((r) => r.competence_month === currentMonthStart) || null
  const estimateCents = Number(currentRow?.total_cents || 0)
  const availableCents = rows.reduce((sum, r) => sum + Number(r.available_cents || 0), 0)
  const paidCents = rows.reduce((sum, r) => sum + Number(r.paid_cents || 0), 0)

  const cards = [
    { label: 'Estimativa (mes atual)', value: formatMoneyFromCents(estimateCents, 'BRL'), tone: 'primary' as const },
    { label: 'Disponivel para payout', value: formatMoneyFromCents(availableCents, 'BRL'), tone: 'success' as const },
    { label: 'Total pago', value: formatMoneyFromCents(paidCents, 'BRL'), tone: 'violet' as const },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-base font-medium">Resumo mensal</div>
        <div className="flex items-center gap-2">
          <Badge variant="soft" className="font-mono">
            Share: {bpsToPercent(currentRow?.user_share_bps || rows?.[0]?.user_share_bps || 0, 2)}
          </Badge>
        </div>
      </div>

      <StatCards items={cards} className="lg:grid-cols-3" />

      <Table>
        <thead>
          <Tr>
            <Th>Mes</Th>
            <Th>Receita liquida</Th>
            <Th>Cap</Th>
            <Th>Share</Th>
            <Th>Total (mes)</Th>
            <Th>Pendente</Th>
            <Th>Disponivel</Th>
            <Th>Reservado</Th>
            <Th>Pago</Th>
            <Th>Status payout</Th>
          </Tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Tr key={r.competence_month}>
              <Td>{monthStartDateToLabel(r.competence_month)}</Td>
              <Td className="font-mono">{formatMoneyFromCents(r.revenue_net_cents, 'BRL')}</Td>
              <Td className="font-mono">{bpsToPercent(r.pool_cap_bps, 2)}</Td>
              <Td className="font-mono">{bpsToPercent(r.user_share_bps, 2)}</Td>
              <Td className="font-mono">{formatMoneyFromCents(r.total_cents, 'BRL')}</Td>
              <Td className="font-mono">{formatMoneyFromCents(r.pending_cents, 'BRL')}</Td>
              <Td className="font-mono">{formatMoneyFromCents(r.available_cents, 'BRL')}</Td>
              <Td className="font-mono">{formatMoneyFromCents(r.reserved_cents, 'BRL')}</Td>
              <Td className="font-mono">{formatMoneyFromCents(r.paid_cents, 'BRL')}</Td>
              <Td>
                {r.payout_status ? (
                  <Badge variant="soft" className="capitalize">
                    {r.payout_status}
                  </Badge>
                ) : (
                  <span className="text-xs text-foreground/60">-</span>
                )}
              </Td>
            </Tr>
          ))}
          {rows.length === 0 && (
            <Tr>
              <Td colSpan={10} className="py-6 text-center text-sm text-foreground/70">
                Sem dados.
              </Td>
            </Tr>
          )}
        </tbody>
      </Table>
    </div>
  )
}

